import { createClient } from '@supabase/supabase-js';

// These should be set in your .env file or injected at runtime by the server
const getRuntimeConfig = () => {
  const config = (window as any).APP_CONFIG || {};
  return config;
};

const runtimeConfig = getRuntimeConfig();
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || runtimeConfig.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || runtimeConfig.VITE_SUPABASE_ANON_KEY || '';

export const isSupabaseConfigured = Boolean(
  supabaseUrl && 
  supabaseAnonKey && 
  supabaseUrl.length > 10
);

export const supabase = isSupabaseConfigured 
  ? createClient(supabaseUrl, supabaseAnonKey)
  : new Proxy({} as any, {
      get: (target, prop) => {
        if (!isSupabaseConfigured) {
          const msg = "Supabase is not configured. Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY.";
          console.error(msg);
          // Return a dummy function for common methods to prevent immediate crashes
          if (['from', 'auth', 'storage'].includes(prop as string)) {
            return () => ({
              select: () => ({ data: null, error: new Error(msg) }),
              insert: () => ({ data: null, error: new Error(msg) }),
              update: () => ({ data: null, error: new Error(msg) }),
              delete: () => ({ data: null, error: new Error(msg) }),
              onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
              getUser: () => Promise.resolve({ data: { user: null }, error: null }),
            });
          }
          return null;
        }
        return target[prop];
      }
    });

// Real email service using our backend API
export const sendConfirmationEmail = async (email: string, type: 'confirmed' | 'cancelled' | 'new_reservation' | 'reminder' | 'welcome' | 'admin_new_user' | 'confirmation', data?: any) => {
  try {
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, type, data }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send email');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Email Error:', error);
    // Fallback to mock behavior in dev if API fails
    console.log(`[EMAIL FALLBACK] Sending ${type} email to ${email}`);
    return { success: true, mocked: true };
  }
};

/**
 * Utility to clear all Supabase related local storage
 * Use this when refresh token errors persist and manual signout fails
 */
export const clearSupabaseAuth = () => {
  if (typeof window !== 'undefined') {
    Object.keys(localStorage).forEach(key => {
      if (key.includes('sb-') || key.includes('supabase.auth')) {
        localStorage.removeItem(key);
      }
    });
    // Also clear cookies if any
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  }
};
